import dedent from 'dedent';

/**
 * System-provided CADET-DSL includes, ordered. Each value MUST begin with a
 * proper %CADET-1.0 prologue; the injector will strip it.
 *
 * Key: include symbol (e.g., 'prelude')
 * Value: CADET-DSL text for that include
 *
 * System-supported attributes for type definitions: 'name' and 'description'.
 * System-supported attributes for instance definitions: 'title'.
 */

/*
Template for translation of IStrings:

'<English>'~en-US '<Spanish>'~es-ES '<German>'~de-DE '<French>'~fr-FR '<Russian>'~ru-RU '<Hindi>'~hi-IN '<Korean>'~ko-KR '<Thai>'~th-TH '<Japanese>'~ja-JP '<Chinese>'~zh-CN '<Hebrew>'~he-IL '<Arabic>'~ar-SA
*/

export const SYSTEM_INCLUDES: readonly (readonly [string, string])[] = [
  [
    'prelude',
    dedent`
      %CADET-1.0

      :UNIT /Generic {
        backgroundColor: 'adaptive-secondary-background'
        isAbstract: true
      }

      :UNIT /Red : /Generic {
        backgroundColor: 'adaptive-red'
        name: 'Red'~en-US 'Rojo'~es-ES 'Rot'~de-DE 'Rouge'~fr-FR 'Красный'~ru-RU 'लाल'~hi-IN '빨간색'~ko-KR 'แดง'~th-TH '赤'~ja-JP '红色'~zh-CN 'אדום'~he-IL 'أحمر'~ar-SA
      }

      :UNIT /Green : /Generic {
        backgroundColor: 'adaptive-green'
        name: 'Green'~en-US 'Verde'~es-ES 'Grün'~de-DE 'Vert'~fr-FR 'Зеленый'~ru-RU 'हरा'~hi-IN '초록색'~ko-KR 'เขียว'~th-TH '緑'~ja-JP '绿色'~zh-CN 'ירוק'~he-IL 'أخضر'~ar-SA
      }

      :UNIT /Blue : /Generic {
        backgroundColor: 'adaptive-blue'
        name: 'Blue'~en-US 'Azul'~es-ES 'Blau'~de-DE 'Bleu'~fr-FR 'Синий'~ru-RU 'नीला'~hi-IN '파란색'~ko-KR 'น้ำเงิน'~th-TH '青'~ja-JP '蓝色'~zh-CN 'כחול'~he-IL 'أزرق'~ar-SA
      }

      :UNIT /Yellow : /Generic {
        backgroundColor: 'adaptive-yellow'
        name: 'Yellow'~en-US 'Amarillo'~es-ES 'Gelb'~de-DE 'Jaune'~fr-FR 'Желтый'~ru-RU 'पीला'~hi-IN '노란색'~ko-KR 'เหลือง'~th-TH '黄色'~ja-JP '黄色'~zh-CN 'צהוב'~he-IL 'أصفر'~ar-SA
      }

      :UNIT /Entity {
        attrs: [
          // The color of the entity header bar
          {
            name: 'headerColor'
            type: 'color'
          }
        ]

        name: 'Entity'
        description: 'A concept with a classification'
        headerColor: 'adaptive-foreground'
        isAbstract: true
      }

      :UNIT /TOCEntity : /Entity {
        name: 'TOC Entity'
        description: 'An entity used in the Theory of Constraints Thinking Processes (TOC-TP)'
        isAbstract: true
      }

      :UNIT /Goal : /TOCEntity {
        name: 'Goal'~en-US 'Meta'~es-ES 'Ziel'~de-DE 'Objectif'~fr-FR 'Цель'~ru-RU 'लक्ष्य'~hi-IN '목표'~ko-KR 'เป้าหมาย'~th-TH '目標'~ja-JP '目标'~zh-CN 'מטרה'~he-IL 'هدف'~ar-SA
        description: 'A goal to achieve something'
        headerColor: 'adaptive-green'
      }

      :UNIT /UDE : /TOCEntity {
        name: 'Undesirable Effect'~en-US 'Efecto indeseable'~es-ES 'Unerwünschte Wirkung'~de-DE 'Effet indésirable'~fr-FR 'Нежелательный эффект'~ru-RU 'अवांछित प्रभाव'~hi-IN '바람직하지 않은 효과'~ko-KR 'ผลกระทบที่ไม่พึงประสงค์'~th-TH '望ましくない効果'~ja-JP '不良影响'~zh-CN 'השפעה בלתי רצויה'~he-IL 'أثر غير مرغوب فيه'~ar-SA
        description: 'An undesirable effect'
        headerColor: 'adaptive-red'
      }

      :UNIT /IE : /TOCEntity {
        name: 'Intermediate Effect'~en-US 'Efecto intermedio'~es-ES 'Zwischeneffekt'~de-DE 'Effet intermédiaire'~fr-FR 'Промежуточный эффект'~ru-RU 'मध्यवर्ती प्रभाव'~hi-IN '중간 효과'~ko-KR 'ผลกระทบขั้นกลาง'~th-TH '中間効果'~ja-JP '中间效应'~zh-CN 'השפעת ביניים'~he-IL 'أثر وسيط'~ar-SA
        description: 'An intermediate effect'
        headerColor: 'adaptive-teal'
      }

      :UNIT /Action : /TOCEntity {
        name: 'Action'~en-US 'Acción'~es-ES 'Aktion'~de-DE 'Action'~fr-FR 'Действие'~ru-RU 'कार्रवाई'~hi-IN '행동'~ko-KR 'การกระทำ'~th-TH '行動'~ja-JP '行动'~zh-CN 'פעולה'~he-IL 'إجراء'~ar-SA
        description: 'An action that may or may not be taken'
        headerColor: 'adaptive-orange'
      }

      :UNIT /Junctor {
        attrs: [
          // The short name of the junctor's function. Drawn in the junctor's circle
          {
            name: 'shortName'
            type: 'istring'
          }

          // The background color of the junctor's circle
          {
            name: 'color'
            type: 'color'
          }
        ]

        name: 'Junctor'
        isAbstract: true
      }

      :UNIT /AND : /Junctor {
        name: 'And'~en-US 'Y'~es-ES 'Und'~de-DE 'Et'~fr-FR 'И'~ru-RU 'और'~hi-IN '논리곱'~ko-KR 'และ'~th-TH '論理積'~ja-JP '与'~zh-CN 'וגם'~he-IL 'و'~ar-SA
        description: 'Logical conjunction'
        shortName: 'AND'~en-US 'Y'~es-ES 'UND'~de-DE 'ET'~fr-FR 'И'~ru-RU 'और'~hi-IN 'AND'~ko-KR 'และ'~th-TH 'AND'~ja-JP '与'~zh-CN 'ו'~he-IL 'و'~ar-SA
        color: '#88f'
      }

      :UNIT /OR : /Junctor {
        name: 'Or'~en-US 'O'~es-ES 'Oder'~de-DE 'Ou'~fr-FR 'Или'~ru-RU 'या'~hi-IN '논리합'~ko-KR 'หรือ'~th-TH '論理和'~ja-JP '或'~zh-CN 'או'~he-IL 'أو'~ar-SA
        description: 'Logical disjunction'
        shortName: 'OR'~en-US 'O'~es-ES 'ODER'~de-DE 'OU'~fr-FR 'ИЛИ'~ru-RU 'या'~hi-IN 'OR'~ko-KR 'หรือ'~th-TH 'OR'~ja-JP '或'~zh-CN 'או'~he-IL 'أو'~ar-SA
        color: '#8f8'
      }

      :UNIT /XOR : /Junctor {
        name: 'Exclusive OR'~en-US 'Disyunción exclusiva'~es-ES 'Exklusives Oder'~de-DE 'Ou exclusif'~fr-FR 'Исключающее ИЛИ'~ru-RU 'एक्सक्लूसिव या'~hi-IN '배타적 논리합'~ko-KR 'หรือแบบเอ็กซ์คลูซีฟ'~th-TH '排他的論理和'~ja-JP '异或'~zh-CN 'או-בלעדי'~he-IL 'أو حصري'~ar-SA
        description: 'Exclusive disjunction'
        shortName: 'XOR'
        color: '#ff8'
      }

      :EDGE -> : /Edge {
        attrs: [
          {
            name: 'thickness'
            type: 'number'
          },
          {
            name: 'color'
            type: 'color'
          }
        ]
        name: 'Unspecified'
        description: 'Edge with unspecified meaning'
        thickness: 1
        color: 'adaptive-foreground'
      }
    `
  ]
];
